import { useEffect, useRef } from 'react';
import { connect } from 'react-redux';

import { setHoverListingId } from 'bundles/connect/components/store/actions/ui';

import { useGoogleMap } from '@react-google-maps/api';
import { PROPERTY_TYPE_ICON } from 'helpers/icon_helpers';
import { setExclusiveListingId } from '../../helpers/navigation_helper';
import type { AllExclusiveListingResults } from 'bundles/connect/types';

function highlight(markerView) {
  markerView.content.classList.add('highlight');
  markerView.element.style.zIndex = 1;
}

function unhighlight(markerView) {
  markerView.content.classList.remove('highlight');
  markerView.element.style.zIndex = 0;
}

type ConnectListingMapMarkerProps = {
  isHovering: boolean;
  largeIcon: boolean;
  result: AllExclusiveListingResults;
  saleListingId: number;
  setHoverListingId: (id?: number) => void;
}

function ConnectListingMapMarker({
  isHovering,
  largeIcon,
  result,
  saleListingId,
  setHoverListingId
}: ConnectListingMapMarkerProps) {
  const map = useGoogleMap();
  const marker = useRef<google.maps.marker.AdvancedMarkerElement>();

  useEffect(() => {
    // Don't create marker if coordinates are missing
    if (!result.location.lat || !result.location.lon) {
      return;
    }

    const markerElement = document.createElement('div');
    markerElement.classList.add('property-type-marker', 'property-type-marker-primary');
    if (largeIcon) {
      markerElement.classList.add('large');
    }

    const icon = PROPERTY_TYPE_ICON[result.propertyTypeId] || PROPERTY_TYPE_ICON[0];

    markerElement.innerHTML = largeIcon && icon ? `<i aria-hidden="true" class="fa-solid ${icon} icon"></i>` : '';
    markerElement.innerHTML += `<div class="marker-shadow"></div>`;

    const position = { lat: result.location.lat, lng: result.location.lon };

    const advancedMarkerView = new google.maps.marker.AdvancedMarkerElement({
      map,
      position: position,
      content: markerElement
    });
    marker.current = advancedMarkerView;
    const element = advancedMarkerView.element;

    ['focus', 'pointerenter'].forEach((eventName) => {
      element.addEventListener(eventName, () => {
        setHoverListingId(saleListingId);
      });
    });

    ['blur', 'pointerleave'].forEach((eventName) => {
      element.addEventListener(eventName, () => {
        setHoverListingId();
      });
    });

    advancedMarkerView.addListener('click', () => {
      setHoverListingId();
      setExclusiveListingId(saleListingId);
    });

    return () => {
      if (marker.current) {
        marker.current.map = null;
      }
    };
  }, [map]);

  useEffect(() => {
    if (!marker.current) return;

    if (isHovering) {
      highlight(marker.current);
    } else {
      unhighlight(marker.current);
    }

  }, [isHovering]);

  return null;
}

const mapStateToProps = ({
  results: { allResults },
  ui: { hoverSaleListingId, mapZoomReadonly }
}, ownProps) => ({
  isHovering: hoverSaleListingId === ownProps.saleListingId,
  largeIcon: mapZoomReadonly >= 15 || (allResults || []).length <= 250
});
const mapDispatchToProps = { setHoverListingId };

export default connect(mapStateToProps, mapDispatchToProps)(ConnectListingMapMarker);
